import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button } from "@mui/joy";
import { Box, Paper, Tooltip } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import {
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Vendor_Entity } from "../../entities/vendor";

import { useAppSelector } from "../hooks";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { useVendors } from "../../redux/vendor/vendorHooks";
import {
  useCreateVendorMutation,
  useDeleteVendorMutation,
  useUpdateVendorMutation,
} from "../../redux/vendor/vendorEndpoints";
import { setFormOpen, setSelectedVendorID } from "../../redux/filmsReducer";

/**
 * Vendor
 *
 * @returns {null} Vendor
 */
export default function Vendors() {
  const { vendors } = useVendors();
  const [deleteVendor] = useDeleteVendorMutation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        apiRef={apiRef}
        rows={vendors ?? []}
        onRowClick={(e) => {
          dispatch(setSelectedVendorID(e.row.id));
          dispatch(setFormOpen({ formID: "vendor", isOpen: true }));
        }}
        columns={vendorColumns(confirm, deleteVendor)}
        showToolbar
        slots={{
          toolbar: VendorToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
      />
    </Paper>
  );
}

function VendorToolbar() {
  const dispatch = useDispatch();

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 1,
          gap: 2,
          alignItems: "center",
          width: "100%",
        }}
      >
        <Tooltip title="ctrl + enter">
          <Button
            size="sm"
            variant="soft"
            onClick={() =>
              dispatch(setFormOpen({ formID: "vendor", isOpen: true }))
            }
          >
            + New Vendor
          </Button>
        </Tooltip>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

const vendorColumns = (
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  deleteVendor: (id: number) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "number",
      width: 40,
    },
    {
      field: "name",
      headerName: "Name",
      editable: true,
      type: "string",
      width: 150,
    },
    {
      field: "contact",
      headerName: "Contact",
      editable: true,
      type: "string",
      width: 150,
    },
    {
      field: "address",
      headerName: "Address",
      editable: true,
      type: "string",
      width: 200,
    },
    {
      field: "city",
      headerName: "City",
      editable: true,
      type: "string",
      width: 100,
    },
    {
      field: "state",
      headerName: "State",
      editable: true,
      type: "string",
      width: 40,
    },
    {
      field: "zipcode",
      headerName: "Zipcode",
      editable: true,
      type: "string",
      width: 80,
    },
    {
      field: "email",
      headerName: "Email",
      editable: true,
      type: "string",
      width: 150,
    },
    {
      field: "phone",
      headerName: "Phone",
      editable: true,
      type: "string",
      width: 150,
    },
    {
      field: "kind",
      headerName: "Kind",
      editable: true,
      type: "string",
      width: 150,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any }) => [
        <GridActionsCellItem
          key="delete"
          showInMenu
          icon={<DeleteIcon />}
          label={`Delete`}
          onClick={() => {
            confirm({
              description: `You are about to delete a vendor`,
            })
              .then(() => {
                deleteVendor(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);
